import { useTranslation } from 'react-i18next'
import {
  FiArrowRight,
  FiCheck,
  FiDownload,
  FiGift,
  FiLogIn,
} from 'react-icons/fi'
import { useLocation, useNavigate } from 'react-router-dom'

const IOS_APP_URL =
  'https://apps.apple.com/es/app/ciclogreen-movilidad/id1326776999'
const ANDROID_APP_URL =
  'https://play.google.com/store/apps/details?id=es.inmovens.ciclogreen'

const detectPlatform = (): 'ios' | 'android' | 'other' => {
  const ua = navigator.userAgent.toLowerCase()
  if (/iphone|ipad|ipod|ios/.test(ua)) return 'ios'
  if (/android/.test(ua)) return 'android'
  return 'other'
}

export default function RegisterConfirmation() {
  const { t } = useTranslation('registerConfirmation')
  const location = useLocation()
  const navigate = useNavigate()
  const accentColor = location.state?.accentColor || '#00B388'

  const handleDownloadApp = () => {
    const platform = detectPlatform()

    if (platform === 'ios') {
      window.location.href = IOS_APP_URL
    } else if (platform === 'android') {
      window.location.href = ANDROID_APP_URL
    } else {
      window.open('/qr', '_blank')
    }
  }

  if (!location.state) {
    navigate('/register')
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div
            className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-6"
            style={{ backgroundColor: `#00C8B415` }}
          >
            <FiCheck className="w-8 h-8 text-blue-800" strokeWidth={3} />
          </div>
          <h1 className="text-2xl font-bold mb-2">{t('title')}</h1>
          <p className="text-gray-600">{t('subtitle')}</p>
        </div>

        <div className="space-y-4">
          <button
            onClick={handleDownloadApp}
            className="w-full bg-white rounded-xl p-4 shadow-sm border-2 text-left transition-all hover:shadow-md"
            style={{ borderColor: accentColor }}
          >
            <div className="flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${accentColor}15` }}
              >
                <FiDownload
                  className="w-6 h-6"
                  style={{ color: accentColor }}
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="text-xs font-semibold"
                    style={{ color: accentColor }}
                  >
                    {t('step1.label')}
                  </span>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full text-white font-medium"
                    style={{ backgroundColor: accentColor }}
                  >
                    {t('step1.badge')}
                  </span>
                </div>
                <h3 className="font-bold text-gray-900 mb-1">
                  {t('step1.title')}
                </h3>
                <p className="text-sm text-gray-500 mb-2">
                  {t('step1.description')}
                </p>
                <div
                  className="flex items-center text-sm font-semibold"
                  style={{ color: accentColor }}
                >
                  {t('step1.action')}
                  <FiArrowRight className="ml-1 w-4 h-4" />
                </div>
              </div>
            </div>
          </button>

          <div className="w-full bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-100">
                <FiLogIn className="w-6 h-6 text-gray-500" />
              </div>
              <div className="flex-1">
                <span className="text-xs font-semibold text-gray-400">
                  {t('step2.label')}
                </span>
                <h3 className="font-bold text-gray-900 mb-1">
                  {t('step2.title')}
                </h3>
                <p className="text-sm text-gray-500">
                  {t('step2.description')}
                </p>
              </div>
            </div>
          </div>

          <div className="w-full bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-100">
                <FiGift className="w-6 h-6 text-gray-500" />
              </div>
              <div className="flex-1">
                <span className="text-xs font-semibold text-gray-400">
                  {t('step3.label')}
                </span>
                <h3 className="font-bold text-gray-900 mb-1">
                  {t('step3.title')}
                </h3>
                <p className="text-sm text-gray-500">
                  {t('step3.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
